import { z } from "zod";

/** Explicit outbound fields, configured by an immutable business-spec version. */
export const DOCUMENT_COMPANY_FIELDS = {
  legal_name: { fr: "Nom de l’entreprise", en: "Company name" },
  industry: { fr: "Nature de l’entreprise", en: "Business activity" },
  founded_year: { fr: "En affaires depuis (année)", en: "In business since (year)" },
  address_street: { fr: "Adresse", en: "Address" },
  address_city: { fr: "Ville", en: "City" },
  address_province: { fr: "Province", en: "Province" },
  address_postal: { fr: "Code postal", en: "Postal code" },
  address_country: { fr: "Pays", en: "Country" },
  phone: { fr: "Téléphone", en: "Phone" },
  fax: { fr: "Télécopieur", en: "Fax" },
  language: { fr: "Langue de correspondance", en: "Correspondence language" },
  plan_admin_name: { fr: "Administrateur du régime", en: "Plan administrator" },
  employee_count: { fr: "Nombre d’employés déclaré", en: "Declared employee count" },
  has_seasonal: { fr: "Employés saisonniers", en: "Seasonal employees" },
  has_commission: {
    fr: "Rémunération sous forme de commissions",
    en: "Commission-based compensation",
  },
  has_unionized: { fr: "Employés syndiqués", en: "Unionized employees" },
  has_remote_abroad: { fr: "Télétravail à l’étranger", en: "Remote work abroad" },
  considers_remote_abroad: {
    fr: "Télétravail à l’étranger envisagé",
    en: "Considering remote work abroad",
  },
  family_ties: {
    fr: "Lien de parenté avec les propriétaires",
    en: "Family relationship with owners",
  },
  has_current_plan: { fr: "Régime actuellement en vigueur", en: "Existing benefit plan" },
  current_insurer: { fr: "Assureur actuel", en: "Current insurer" },
  current_policy_number: { fr: "Numéro de police actuel", en: "Current policy number" },
  current_since_date: { fr: "Régime en vigueur depuis", en: "Plan in effect since" },
  current_renewal_date: { fr: "Date de renouvellement actuelle", en: "Current renewal date" },
  effect_date: { fr: "Date d’effet du dossier", en: "Project effective date" },
} as const;
export type DocumentCompanyField = keyof typeof DOCUMENT_COMPANY_FIELDS;
const keys = Object.keys(DOCUMENT_COMPANY_FIELDS) as [
  DocumentCompanyField,
  ...DocumentCompanyField[],
];
export const DocumentProfileSchema = z
  .object({
    company_fields: z
      .array(z.enum(keys))
      .min(1)
      .max(keys.length)
      .refine((v) => new Set(v).size === v.length, "duplicate company field"),
  })
  .strict();
export type DocumentProfile = z.infer<typeof DocumentProfileSchema>;
export const DocumentProfileSnapshotSchema = z
  .object({
    version: z.literal(1),
    language: z.enum(["fr", "en"]),
    company_name: z.string().max(1000),
    project_title: z.string().max(1000),
    request_title: z.string().max(1000),
    cabinet_name: z.string().max(1000),
    primary_color: z.string().regex(/^#[0-9A-F]{6}$/),
    fields: z
      .array(
        z
          .object({
            key: z.enum(keys),
            value: z.union([z.string().max(1000), z.number().finite(), z.boolean(), z.null()]),
          })
          .strict(),
      )
      .min(1)
      .max(keys.length)
      .refine(
        (fields) => new Set(fields.map((f) => f.key)).size === fields.length,
        "duplicate profile field",
      ),
  })
  .strict();
export type DocumentProfileSnapshot = z.infer<typeof DocumentProfileSnapshotSchema>;
export function documentProfile(schema: unknown): DocumentProfile | undefined {
  const value = (schema as { version_metadata?: { document_profile?: unknown } } | null)
    ?.version_metadata?.document_profile;
  return value === undefined ? undefined : DocumentProfileSchema.parse(value);
}
